import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Spin, Table, Tag } from 'antd';
import { mdiAccountPlus, mdiAccountRemove, mdiAccountCancel, mdiAccountCheck } from '@mdi/js';
import { UI54Card } from '@agpl/ui54kit';

import { ApiContext } from '../../api/ApiContextProvider';
import { antNotification } from '../../mainUtils';
import AntButtonWithMDI from '../../components/AntButtonWithMDI';

const columns = [
  {
    title: 'Service',
    dataIndex: 'service_name',
    key: 'service_name',
  },
  {
    title: 'Status',
    key: 'status',
    width: 110,
    render: (_, row) => {
      if (row.error) return <Tag color="error">Not found</Tag>;
      if (row.noSync) return <Tag color="error">No sync</Tag>;
      return <Tag color={row.status ? 'success' : 'processing'}>{row.status ? 'Synchronized' : 'Pending'}</Tag>;
    },
  },
  {
    title: 'Method',
    dataIndex: 'delivery_method',
    key: 'delivery_method',
    width: 100,
    render: (val) => val || '—',
  },
  {
    title: 'Time',
    dataIndex: 'created',
    key: 'created',
    render: (val) => (val ? new Date(val).toLocaleTimeString() : '—'),
  },
];

const typeColors = { create: 'blue', delete: 'red' };

function getRunLabel(run) {
  if (run.type === 'create') return 'Create actor';
  if (run.type === 'delete') return 'Delete actor';
  return run.banAction === 'ban' ? 'Ban actor' : 'Unban actor';
}

function TestRun({ run }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        marginBottom: 6, fontSize: 12, color: '#8c8c8c', display: 'flex', alignItems: 'center', gap: 8,
      }}
      >
        <Tag color={typeColors[run.type] || 'orange'}>
          {getRunLabel(run)}
        </Tag>
        <span>{`Test user: ${run.testUser}`}</span>
        {run.loading && <Spin size="small" />}
      </div>
      <Table
        dataSource={run.results}
        columns={columns}
        rowKey="service_name"
        pagination={false}
        size="small"
        bordered
      />
    </div>
  );
}

TestRun.propTypes = {
  run: PropTypes.shape({
    type: PropTypes.string,
    banAction: PropTypes.string,
    testUser: PropTypes.string,
    loading: PropTypes.bool,
    results: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

TestRun.defaultProps = {
  run: {},
};

function TestSyncPage() {
  const {
    requestTestSync, pollTestSync,
    requestDeleteSync, pollDeleteSync,
    checkTestActorExists,
    requestBanSync, pollBanSync,
  } = useContext(ApiContext);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [, setTestActorExists] = useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    checkTestActorExists()
      .then((data) => {
        setTestActorExists(data.exists);
        setIsBanned(data.is_banned);
      })
      .catch(() => {});
  }, []);

  const stopPolling = () => {
    clearInterval(intervalRef.current);
    clearTimeout(timeoutRef.current);
  };

  const updateRun = (id, patch) => {
    setRuns((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const fns = {
    create: { request: requestTestSync, poll: pollTestSync },
    delete: { request: requestDeleteSync, poll: pollDeleteSync },
    ban: { request: requestBanSync, poll: pollBanSync },
  };

  const runTest = (type) => {
    stopPolling();
    setRunning(true);

    const runId = Date.now();
    const { request: requestFn, poll: pollFn } = fns[type];

    setRuns((prev) => [{
      id: runId, type, testUser: '…', results: [], loading: true,
    }, ...prev]);

    requestFn()
      .then((data) => {
        if (type === 'create') setTestActorExists(true);
        if (type === 'delete') { setTestActorExists(false); setIsBanned(false); }
        if (type === 'ban') {
          setTestActorExists(true);
          setIsBanned(data.action === 'ban');
          updateRun(runId, { banAction: data.action });
        }
        const uuid = data.test_user;
        const services = data.services || [];
        const initial = services.map((s) => ({
          service_name: s.service_name,
          status: false,
          error: s.error || null,
        }));
        const notFound = initial.filter((s) => s.error);

        updateRun(runId, { testUser: uuid, results: initial });

        if (services.length === 0) {
          updateRun(runId, { loading: false });
          setRunning(false);
          return;
        }

        timeoutRef.current = setTimeout(() => {
          stopPolling();
          setRuns((prev) => prev.map((r) => {
            if (r.id !== runId) return r;
            return {
              ...r,
              loading: false,
              results: r.results.map((s) => (!s.status && !s.error ? { ...s, noSync: true } : s)),
            };
          }));
          setRunning(false);
        }, 5000);

        intervalRef.current = setInterval(() => {
          pollFn(uuid).then((res) => {
            const items = res.results || [];
            updateRun(runId, { results: [...notFound, ...items] });
            if (items.length > 0 && items.every((r) => r.status)) {
              stopPolling();
              updateRun(runId, { loading: false });
              setRunning(false);
            }
          });
        }, 3000);
      })
      .catch(() => {
        antNotification.error(`${type === 'create' ? 'Create' : 'Delete'} sync test failed`);
        updateRun(runId, { loading: false });
        setRunning(false);
      });
  };

  return (
    <UI54Card>
      <Row gutter={8}>
        <Col>
          <AntButtonWithMDI
            pathForMdi={mdiAccountPlus}
            label="Create actor"
            onClick={() => runTest('create')}
            disabled={running}
            className="button-primary-outlined"
          />
        </Col>
        <Col>
          <AntButtonWithMDI
            pathForMdi={mdiAccountRemove}
            label="Delete actor"
            onClick={() => runTest('delete')}
            disabled={running}
            className="button-danger-outlined"
          />
        </Col>
        <Col>
          <AntButtonWithMDI
            pathForMdi={isBanned ? mdiAccountCheck : mdiAccountCancel}
            label={isBanned ? 'Unban actor' : 'Ban actor'}
            onClick={() => runTest('ban')}
            disabled={running}
            className={isBanned ? 'button-primary-outlined' : 'button-warning-outlined'}
          />
        </Col>
      </Row>

      {runs.map((run) => (
        <TestRun key={run.id} run={run} />
      ))}
    </UI54Card>
  );
}

export default TestSyncPage;
